import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { createContext, useEffect, useMemo, useState } from 'react';

// analytics
import { getAnalytics, setUserId } from "firebase/analytics";

// import { AES } from 'crypto-js';
import hmacsha256 from 'crypto-js/hmac-sha256';


// hook
import useApi from '../hooks/api/useApi';
import useLocalStorage from '../hooks/useLocalStorage';
//
import { endpoints } from '../configs/api';
import { PATH_AUTH } from '../routes/paths';

import { userIdHashKey } from '../configs/firesBaseConfig';

// -----------------------------------------------

const initialState = {
  auth: null,
  facilities: [],
  selectedFacility: null,
};

const DEFAULT_FACILITY = {
  id: "",
  name: "",
  avatar: null,
  integration_type: "QR",
}

const AuthContext = createContext({
  ...initialState,
  setAuth: () => { },
  getOTP: () => { },
  verifyOTP: () => { },
  resendOTP: () => { },
  login: () => { },
  logout: () => { },
  resetAuth: () => { },
  setSelectedFacility: () => { },
  createFacility: () => { },
});

// -----------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

let analytics;

try {
  if (process.env.REACT_APP_ENV === 'dev') {
    console.log("Analytics disabled in dev mode");
    analytics = null; // Explicitly set to null
  } else {
    analytics = getAnalytics();
  }
} catch (error) {
  console.error("Failed to initialize analytics:", error);
  analytics = null; // Ensure analytics is always defined
}

function AuthProvider({ children }) {
  const { enqueueSnackbar } = useSnackbar();
  const { fetchResponse } = useApi();

  // const [state, dispatch] = useReducer(reducer, initialState);

  const [auth, setAuth] = useLocalStorage('auth', null);

  // facilities
  const [facilities, setFacilities] = useState([]);
  // const [facilities, setFacilities] = useLocalStorage('facilities');
  const [selectedFacility, setSelectedFacility] = useLocalStorage('selectedFacility');




  // --------- OTP --------
  const getOTP = (phone) => {

    const payload = {
      "phone_number": phone,
    }

    return fetchResponse(endpoints.getOTP, 'POST', payload)
      .then(res => {
        if (res.status === 200) {
          return true;
        }
        if (res.status === 403) {
          throw new Error(res?.data?.message || 'Unable to send the OTP');
        }
        if (res.status === 410) {
          throw new Error(res?.data?.message || 'This account no longer exists.');
        }
        throw new Error('Unable to send the OTP');
      })

  };

  const resendOTP = (phone) => {

    const payload = {
      "phone_number": phone,
    }

    return fetchResponse(endpoints.resendOTP, 'POST', payload)
      .then(res => {
        // console.log(res)
        if (res.status === 200) {
          return true;
        }
        if (res.status === 403) {
          throw new Error(res?.data?.message || 'Unable to send the OTP');
        }
        if (res.status === 410) {
          throw new Error(res?.data?.message || 'This account no longer exists.');
        }
        throw new Error('Unable to send the OTP');
      })

  };

  const verifyOTP = async (payload) => {
    try {
      const response = await fetchResponse(endpoints.verifyOTP, 'POST', payload);
      const { data } = response;

      if (!data?.token) {
        throw new Error(data?.message || 'Unable to sign in.');
      }

      // Set authentication data
      setAuth({ ...data });

      // Only proceed with analytics if it's available
      if (analytics) {
        try {
          const hashkey = await userIdHashKey;
          const hash = hmacsha256(data?.user_id, hashkey).toString();
          await setUserId(analytics, hash);
        } catch (hashError) {
          console.error('Error setting user analytics:', hashError);
          // Non-critical failure (proceed without analytics)
        }
      } else {
        console.warn('Analytics not initialized - skipping user ID hashing');
      }

      // Handle facility selection for non-admin users
      if (data?.user_type !== "SOFTWARE_ADMIN" && data.facility?.length > 0) {
        if (!selectedFacility) {
          setSelectedFacility({ ...data.facility[0] });
        }
      }

      return data.token;
    } catch (error) {
      console.error('Error in verifyOTP:', error.message);
      throw new Error(error.response?.data?.message || error.message || 'Authentication failed');
    }
  };
  // --------- OTP --------


  const logout = async (api = true, callbackUrl = null) => {
    try {
      if (!api) {
        resetAuth(callbackUrl);
        return;
      }

      if (!auth?.token) {
        resetAuth(callbackUrl);
        return;
      }

      try {
        await fetchResponse(
          endpoints.logout,
          "GET",
          {},
          { 'Authorization': `Bearer ${auth.token}` }
        );
      } catch (err) {
        console.error('Logout API error:', err.message);
        // Continue with logout even if API fails
      } finally {
        resetAuth(callbackUrl);
      }
    } catch (error) {
      console.error('Unexpected logout error:', error);
      resetAuth(callbackUrl);
    }
  };


  const resetAuth = (callbackUrl) => {
    try {
      // Clear auth-related storage items
      const itemsToRemove = [
        "auth",
        "facilities",
        "selectedFacility",
        "fcmDeviceToken"
      ];

      itemsToRemove.forEach(item => {
        try {
          window.localStorage.removeItem(item);
        } catch (storageError) {
          console.error(`Failed to remove ${item} from storage:`, storageError);
        }
      });

      // Reset auth state
      try {
        setAuth(null);
      } catch (stateError) {
        console.error('Failed to reset auth state:', stateError);
      }

      // Schedule redirect with cleanup
      const redirect = () => {
        try {
          const targetUrl = callbackUrl || PATH_AUTH.login;
          // Use window.location.assign() instead of direct assignment
          window.location.assign(targetUrl);
        } catch (redirectError) {
          console.error('Redirect failed:', redirectError);
          // Fallback to default login path if specific redirect fails
          window.location.assign(PATH_AUTH.login);
        }
      };

      // Use slightly longer timeout for more reliable state cleanup
      setTimeout(redirect, 100);

    } catch (globalError) {
      console.error('Critical error in resetAuth:', globalError);
      // Last resort full redirect attempt
      window.location.assign(PATH_AUTH.login);
    }
  };
  // --------- login logout --------



  // --------- Facility Management --------
  const fetchFacility = async () => {
    try {
      // Validate required auth data exists
      if (!auth?.user_id) {
        throw new Error('Authentication data not available');
      }

      const response = await fetchResponse(
        endpoints.facility.find,
        "POST",
        { software_admin: auth.user_id }
      );

      const { data } = response;

      // Validate response data structure
      if (!Array.isArray(data)) {
        throw new Error('Invalid facilities data format');
      }

      // Process facilities only if we received some
      if (data.length > 0) {
        const enrichedFacilities = data.map(facility => ({
          ...DEFAULT_FACILITY,
          ...facility
        }));

        setFacilities(enrichedFacilities);

        // Set default facility if none selected
        if (!selectedFacility && enrichedFacilities[0]) {
          setSelectedFacility({
            ...DEFAULT_FACILITY,
            ...enrichedFacilities[0]
          });
        }
      }

    } catch (error) {
      console.error('Failed to fetch facilities:', {
        error: error.message,
        userId: auth?.user_id,
        endpoint: endpoints.facility.find
      });

      // Optional: Set empty state or error state
      setFacilities([]);
    }
  };

  const createFacility = async (payload) => {
    try {
      // Validate required auth data
      if (!auth?.user_id || !auth?.token) {
        throw new Error('Authentication data not available');
      }

      // Prepare payload
      const requestPayload = {
        ...payload,
        software_admin: auth.user_id
      };

      // Make API call
      const response = await fetchResponse(
        endpoints.facility.create,
        "POST",
        requestPayload,
        { 'Authorization': `Bearer ${auth.token}` }
      );

      const { data } = response;

      // Update facilities state
      setFacilities(prev => [...(prev || []), data]);

      // Set as selected if none selected
      if (!selectedFacility) {
        setSelectedFacility({ ...DEFAULT_FACILITY, ...data });
      }

      // Show success notification
      enqueueSnackbar('Facility created successfully', {
        variant: 'success',
        autoHideDuration: 3000
      });

      // Refresh facilities list
      await fetchFacility();

    } catch (error) {
      console.error('Facility creation failed:', {
        error: error.message,
        payload,
        endpoint: endpoints.facility.create
      });

      enqueueSnackbar(error.response?.data?.message || 'Failed to create facility', {
        variant: 'error',
        autoHideDuration: 5000
      });
    }
  };
  // --------- Facility --------





  useEffect(() => {
    if (!auth) {
      return;
    }

    // console.log('loger/ac/108 auth changed', auth);
    if (auth.user_type === "SOFTWARE_ADMIN") {
      fetchFacility();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const authContextProviderValue = useMemo(() => ({
    // ...state,

    // TODO, this is of no use
    user: {
      token: auth?.token,
      photoURL: "",
      email: "",
      name: auth?.name,
      user_id: auth?.user_id,
      phone_number: auth?.phone_number,
      user_type: auth?.user_type,
    },

    auth,
    setAuth,

    getOTP,
    resendOTP,
    verifyOTP,

    // login,
    logout,
    resetAuth,

    facilities,
    selectedFacility,
    setSelectedFacility,
    createFacility,
  }),
    // eslint-disable-next-line
    [
      auth,
      facilities,
      selectedFacility,
    ]);

  return (
    <AuthContext.Provider
      value={authContextProviderValue}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
